import { Box, Typography, useTheme } from '@mui/material';
import { type MouseEvent, type ReactNode } from 'react';

import {
  DiagramNodeFill,
  GraphVizIconShape,
  type TileVariant,
} from '@/components/diagrams/diagramStyle';
import { COLORS } from '@/styles/tokens/colors';

import { getTilePalette } from '../Tile/Tile.utils';

/**
 * The outline of each shape in a 100 × 100 box. The SVG stretches the
 * outline to the size of the node. The triangle points down, as in the
 * Figma handoff. The rounded rectangle is twice as wide as it is tall, so
 * its corner radius is half as large on the x axis as on the y axis. After
 * the stretch, the corners are round.
 */
const SHAPE_OUTLINES: Record<GraphVizIconShape, ReactNode> = {
  [GraphVizIconShape.Circle]: <ellipse cx="50" cy="50" rx="49" ry="49" />,
  [GraphVizIconShape.Square]: <rect x="1" y="1" width="98" height="98" />,
  [GraphVizIconShape.Rounded]: (
    <rect x="1" y="1" width="98" height="98" rx="10" ry="20" />
  ),
  [GraphVizIconShape.Diamond]: <polygon points="50,1 99,50 50,99 1,50" />,
  [GraphVizIconShape.Triangle]: <polygon points="1,1 99,1 50,99" />,
};

/**
 * The height of each shape as a fraction of its width. A rectangle is twice
 * as wide as it is tall. The triangle is 150 × 126, as in the Figma shape
 * components. The other shapes fit a square.
 */
export const SHAPE_ASPECT_RATIO: Record<GraphVizIconShape, number> = {
  [GraphVizIconShape.Circle]: 1,
  [GraphVizIconShape.Square]: 1,
  [GraphVizIconShape.Rounded]: 0.5,
  [GraphVizIconShape.Diamond]: 1,
  [GraphVizIconShape.Triangle]: 126 / 150,
};

/** The space between the label and a straight outline, in percent of the shape size. */
const LABEL_PADDING = 8;
/**
 * The largest square in a circle has sides of width / √2. That leaves
 * (1 − 1/√2) / 2 of the width, about 15%, on each side.
 */
const CIRCLE_INSET = ((1 - Math.SQRT1_2) / 2) * 100;
/** The largest square in a diamond has sides of half its width. */
const DIAMOND_INSET = 25;
/**
 * The largest rectangle in a triangle is half its width and half its
 * height, at the wide end. The label box of the down-pointing triangle
 * starts below the top edge and ends at half the height. At that height,
 * the triangle is half its width, so each side leaves 25%.
 */
const TRIANGLE_INSET = { side: 25, bottom: 50 };

/**
 * The inset of the label box, which keeps the label inside the outline. Top
 * and bottom are percentages of the shape height, and left and right of its
 * width, so the values match the 100 × 100 outline coordinates.
 */
const LABEL_INSET: Record<GraphVizIconShape, string> = {
  [GraphVizIconShape.Circle]: `${CIRCLE_INSET}%`,
  [GraphVizIconShape.Square]: `${LABEL_PADDING}%`,
  [GraphVizIconShape.Rounded]: `${LABEL_PADDING}%`,
  [GraphVizIconShape.Diamond]: `${DIAMOND_INSET}%`,
  [GraphVizIconShape.Triangle]: `${LABEL_PADDING}% ${TRIANGLE_INSET.side}% ${TRIANGLE_INSET.bottom}%`,
};

/** The width of a shape outline, in canvas pixels. */
const STROKE_WIDTH = 1;

export interface DiagramShapeProps {
  shape: GraphVizIconShape;
  fill?: DiagramNodeFill | null;
  variant: TileVariant;
  /** An opaque hex color. It replaces the colors of `variant`. */
  customColor?: string;
  label: ReactNode;
  /** The width of the shape, in canvas pixels. The height follows the shape. */
  width: number;
  isSelected?: boolean;
  onDoubleClick?: (event: MouseEvent<HTMLElement>) => void;
}

/**
 * A diagram node drawn as a shape with its label inside, as in the Figma
 * shape components. A filled shape has the light shade of its color and no
 * border. An outlined shape is white, with a border in the same light shade.
 * The label stays dark on both. A selected shape has a blue border.
 */
export function DiagramShape({
  shape,
  fill,
  variant,
  customColor,
  label,
  width,
  isSelected = false,
  onDoubleClick,
}: DiagramShapeProps) {
  const theme = useTheme();
  const palette = getTilePalette(theme, variant, customColor);
  const isOutlined = fill === DiagramNodeFill.Outlined;
  let stroke = 'none';
  if (isSelected) stroke = COLORS.BLUE[600];
  else if (isOutlined && palette) stroke = palette.lighter;
  return (
    <Box
      onDoubleClick={onDoubleClick}
      sx={{
        position: 'relative',
        width,
        height: width * SHAPE_ASPECT_RATIO[shape],
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          overflow: 'visible',
          // Keep the outline width when the SVG stretches the shape.
          '& *': { vectorEffect: 'non-scaling-stroke' },
        }}
      >
        <g
          fill={isOutlined ? COLORS.PRIMITIVES.WHITE : palette?.lighter}
          stroke={stroke}
          strokeWidth={STROKE_WIDTH}
        >
          {SHAPE_OUTLINES[shape]}
        </g>
      </Box>
      <Typography
        variant="title-xs"
        component="div"
        sx={{
          position: 'absolute',
          inset: LABEL_INSET[shape],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflowWrap: 'anywhere',
          color: COLORS.GRAY[800],
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
